import {
  Button,
  Chip,
  Paper,
  Typography,
  Zoom,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import Autosuggest from 'react-autosuggest';
import { useDispatch, useSelector } from 'react-redux';
import { TransitionGroup } from 'react-transition-group';

import {
  getRenderExpertSuggestionFunction,
  getSuggestionValue,
  renderInputComponent,
} from 'FRONTEND/components/Calendar/AppointmentForm/stepperHelper';
import StepTemplate from 'FRONTEND/components/Calendar/AppointmentForm/StepTemplate';
import Loader from 'FRONTEND/components/Loader';
import { pickAdditionalExpert, resetAdditionalExpertList, unpickAdditionalExpert } from 'FRONTEND/reducers/bookingSlice';
import {
  clearSearch as clearExpertSearch,
  searchRequest as expertSearchRequest,
} from 'FRONTEND/reducers/expertSlice';
import { selectPatientById } from 'FRONTEND/reducers/patientSlice';
import {
  selectAdditionalSelectedExperts,
  selectUnselectedMatchingExperts,
  selectUserAndSelectedExpertsCommonTenantIds,
} from 'FRONTEND/selectors/expertSelectors';
import { EXPERT_MINIMUM_SEARCH_CHARACTERS, MAX_SEARCH_RESULT_NUMBER } from 'FRONTEND/utils/constants';
import i18n from 'FRONTEND/utils/i18n';

export const MultipleExpertStep = ({
  isCompleted,
  isLocked = false,
  onStepClick = () => {},
  shouldReset = false,
  onStepCompleted = () => {},
  ...otherProps
}) => {
  const dispatch = useDispatch();
  const additionalSelectedExpertList = useSelector(selectAdditionalSelectedExperts);
  const matchingExpertList = useSelector(selectUnselectedMatchingExperts);
  const isLoading = useSelector((state) => state.app.requestCounter[expertSearchRequest.toString()] > 0);
  const [expertQuery, setExpertQuery] = useState('');
  const selectedPatient = useSelector((state) => selectPatientById(state, state.booking.selectedPatientId));
  const isUnrestrictedMode = useSelector((state) => state.calendar.isUnrestrictedMode);

  useEffect(() => {
    if (shouldReset) {
      setExpertQuery('');
      dispatch(resetAdditionalExpertList());
    }
  }, [shouldReset]);

  const handleExpertSelected = (id) => {
    dispatch(pickAdditionalExpert({ id }));
    setExpertQuery('');
  };

  const handleUnpickExpert = (id) => {
    dispatch(unpickAdditionalExpert({ id }));
  };

  const handleConfirmClick = () => {
    onStepCompleted();
  };

  const selectedType = useSelector((state) => state.booking.selectedType);
  const tenantIds = useSelector(selectUserAndSelectedExpertsCommonTenantIds);
  const expertSuggestProps = {
    renderInputComponent,
    suggestions: matchingExpertList.slice(0, MAX_SEARCH_RESULT_NUMBER),
    onSuggestionsFetchRequested: ({ value }) => value.length >= EXPERT_MINIMUM_SEARCH_CHARACTERS && dispatch(expertSearchRequest({
      name: value,
      appointmentType: isUnrestrictedMode ? null : selectedType,
      tenantId: selectedPatient?.tenantId || tenantIds,
    })),
    onSuggestionsClearRequested: () => dispatch(clearExpertSearch()),
    getSuggestionValue,
    renderSuggestion: getRenderExpertSuggestionFunction(handleExpertSelected),
    onSuggestionSelected: (event, { suggestion, method }) => method === 'enter' || method === 'click' ? handleExpertSelected(suggestion.id) : null,
  };

  const additionalExpertAliases = additionalSelectedExpertList.map((expert) => expert?.alias);

  return (
    <StepTemplate
      label={i18n._('Select practitioners')}
      isLocked={isLocked}
      onEditClick={() => onStepClick()}
      isStepCompleted={additionalSelectedExpertList.length > 0 && isCompleted}
      editText={additionalExpertAliases.join(', ')}
      {...otherProps}
    >
      {additionalSelectedExpertList.length > 0 && (
        <div className='appointmentForm-line'>
          <TransitionGroup component={null}>
            {additionalSelectedExpertList.map((selectedExpert) => (
              <Zoom key={selectedExpert.id}>
                <Chip
                  color='primary'
                  variant='outlined'
                  onDelete={() => handleUnpickExpert(selectedExpert.id)}
                  label={selectedExpert.alias || ''}
                  className='appointmentForm-horizontalListItem appointmentForm-noWrapItem'
                />
              </Zoom>
            ))}
          </TransitionGroup>
        </div>
      )}
      <div className='contactInput'>
        <Autosuggest
          open
          {...expertSuggestProps}
          inputProps={{
            autoFocus: true,
            placeholder: i18n._('Enter a practitioner’s name (minimum 3 characters)'),
            value: expertQuery,
            onChange: (event, { newValue }) => setExpertQuery(newValue),
          }}
          renderSuggestionsContainer={({ children, containerProps }) => (
            matchingExpertList.length > 0 ? (
              <Paper {...containerProps} square>
                {children}
              </Paper>
            ) : isLoading ? (
              <Loader isLoading={isLoading} position='verticalFlow' />
            ) : expertQuery && (
              <Typography>{i18n._('No expert matches your search.')}</Typography>
            )
          )}
        />
      </div>
      <div className='appointmentForm-line'>
        <Button
          variant='contained'
          onClick={handleConfirmClick}
          disabled={additionalSelectedExpertList.length === 0}
        >
          {i18n._('Confirm')}
        </Button>
      </div>
    </StepTemplate>
  );
};
